import { z } from "zod";

const stripTrailingSlash = (url: string): string => {
  return url.replace(/\/+$/, "");
};

const stripScheme = (url: string): string => {
  return url.replace(/^https?:\/\//, "");
};

const normalizeDomain = (domain: string): string => {
  return stripScheme(stripTrailingSlash(domain));
};

export const environmentSchema = z.object({
  AUTH0_DOMAIN: z.string().min(1).transform(normalizeDomain),
  AUTH0_CLIENT_ID: z.string().min(1),
  AUTH0_GRANT_TYPE: z.string().min(1),
  AUTH0_AUDIENCE: z.string().min(1),
  AUTH0_SCOPE: z.string().min(1),
  AUTH0_MOCK: z.boolean(),
  // Dev-only: layer the AgenticProvisioner role onto a real Auth0 session so
  // provisioner-only surfaces are reachable locally without an Auth0 role change.
  // Forced false in production builds (see the guard in environment()).
  DEV_MOCK_PROVISIONER: z.boolean(),
  BASE_URL: z.string().min(1),
  INGEST_URL: z.string().min(1),
  APP_VERSION: z.string().default("dev"),
  // Pacific deployment tier for the shell environment banner (not the in-flow
  // Christmas/MyPay company environment). Slugs: production | sandbox | uat |
  // preview | local | development | dev. Empty / missing → unknown (red)
  // fail-safe banner — never silently imply Local.
  DEPLOYMENT_ENVIRONMENT: z
    .string()
    .default("")
    .transform((value) => value.trim()),
  LAUNCHDARKLY_CLIENT_ID: z.string().default(""),
  // URL path prefix the SPA is served from (e.g. "/" in prod/sandbox or
  // "/<pr-number>" in preview). Used as React Router's basename and as the
  // anchor for auth redirects so previews can share a host.
  BASE_PATH: z.string().default("/"),
  SHADOW_RUN_AGENT_ID: z.string().default("").transform((value) => value.trim()),
  // Onboarding agent + its execution service account, used to invoke onboarding
  // Temporal workflows (company-shell provisioning, pay component import) via
  // POST /api/v1/agents/{id}/invocations. Both are required for an invocation.
  ONBOARDING_AGENT_ID: z.string().default("").transform((value) => value.trim()),
  ONBOARDING_SERVICE_ACCOUNT_ID: z
    .string()
    .default("")
    .transform((value) => value.trim()),
  // Doc2Company agent + its execution service account, used by the Bulk create
  // company document upload to turn a payroll implementation questionnaire into
  // company rows. Separate from ONBOARDING_* because the service account needs
  // a `markdown.retrieve` grant over the org documents are uploaded into, which
  // the onboarding workflow account does not hold.
  DOC2COMPANY_AGENT_ID: z.string().default("").transform((value) => value.trim()),
  DOC2COMPANY_SERVICE_ACCOUNT_ID: z
    .string()
    .default("")
    .transform((value) => value.trim()),
});
export type EnvironmentSchema = z.infer<typeof environmentSchema>;

interface RunTimeEnv {
  AgenticAI: {
    Web: { [K in keyof EnvironmentSchema]: string };
  };
}

interface ImportMetaEnv {
  VITE_AUTH0_DOMAIN: string;
  VITE_AUTH0_CLIENT_ID: string;
  VITE_AUTH0_GRANT_TYPE: string;
  VITE_AUTH0_AUDIENCE: string;
  VITE_AUTH0_SCOPE: string;
  VITE_AUTH0_MOCK: string;
  VITE_DEV_MOCK_PROVISIONER: string;
  VITE_BASE_URL: string;
  VITE_INGEST_URL: string;
  VITE_APP_VERSION?: string;
  VITE_DEPLOYMENT_ENVIRONMENT?: string;
  VITE_LAUNCHDARKLY_CLIENT_ID?: string;
  VITE_BASE_PATH?: string;
  VITE_SHADOW_RUN_AGENT_ID?: string;
  VITE_ONBOARDING_AGENT_ID?: string;
  VITE_ONBOARDING_SERVICE_ACCOUNT_ID?: string;
  VITE_DOC2COMPANY_AGENT_ID?: string;
  VITE_DOC2COMPANY_SERVICE_ACCOUNT_ID?: string;
}

const extractImportMetaEnv = (env: ImportMetaEnv): EnvironmentSchema => {
  return environmentSchema.parse({
    AUTH0_DOMAIN: env.VITE_AUTH0_DOMAIN,
    AUTH0_CLIENT_ID: env.VITE_AUTH0_CLIENT_ID,
    AUTH0_GRANT_TYPE: env.VITE_AUTH0_GRANT_TYPE,
    AUTH0_AUDIENCE: stripTrailingSlash(env.VITE_AUTH0_AUDIENCE),
    AUTH0_SCOPE: env.VITE_AUTH0_SCOPE,
    AUTH0_MOCK: env.VITE_AUTH0_MOCK === "true",
    DEV_MOCK_PROVISIONER: env.VITE_DEV_MOCK_PROVISIONER === "true",
    BASE_URL: env.VITE_BASE_URL,
    INGEST_URL: env.VITE_INGEST_URL,
    APP_VERSION: env.VITE_APP_VERSION || "dev",
    // Missing/empty → "" so the banner shows unknown/red (not a false Local).
    // Set VITE_DEPLOYMENT_ENVIRONMENT=local explicitly in local `.env`.
    DEPLOYMENT_ENVIRONMENT: env.VITE_DEPLOYMENT_ENVIRONMENT || "",
    LAUNCHDARKLY_CLIENT_ID: env.VITE_LAUNCHDARKLY_CLIENT_ID || "",
    BASE_PATH: env.VITE_BASE_PATH || "/",
    SHADOW_RUN_AGENT_ID: env.VITE_SHADOW_RUN_AGENT_ID || "",
    ONBOARDING_AGENT_ID: env.VITE_ONBOARDING_AGENT_ID || "",
    ONBOARDING_SERVICE_ACCOUNT_ID: env.VITE_ONBOARDING_SERVICE_ACCOUNT_ID || "",
    DOC2COMPANY_AGENT_ID: env.VITE_DOC2COMPANY_AGENT_ID || "",
    DOC2COMPANY_SERVICE_ACCOUNT_ID:
      env.VITE_DOC2COMPANY_SERVICE_ACCOUNT_ID || "",
  });
};

const extractRunTimeEnv = (env: RunTimeEnv): EnvironmentSchema => {
  const web = env.AgenticAI.Web;
  return environmentSchema.parse({
    AUTH0_DOMAIN: web.AUTH0_DOMAIN,
    AUTH0_CLIENT_ID: web.AUTH0_CLIENT_ID,
    AUTH0_GRANT_TYPE: web.AUTH0_GRANT_TYPE,
    AUTH0_AUDIENCE: stripTrailingSlash(web.AUTH0_AUDIENCE),
    AUTH0_SCOPE: web.AUTH0_SCOPE,
    AUTH0_MOCK: web.AUTH0_MOCK === "true",
    DEV_MOCK_PROVISIONER: web.DEV_MOCK_PROVISIONER === "true",
    BASE_URL: web.BASE_URL,
    INGEST_URL: web.INGEST_URL,
    APP_VERSION: web.APP_VERSION || "dev",
    DEPLOYMENT_ENVIRONMENT: web.DEPLOYMENT_ENVIRONMENT || "",
    LAUNCHDARKLY_CLIENT_ID: web.LAUNCHDARKLY_CLIENT_ID || "",
    BASE_PATH: web.BASE_PATH || "/",
    SHADOW_RUN_AGENT_ID: web.SHADOW_RUN_AGENT_ID || "",
    ONBOARDING_AGENT_ID: web.ONBOARDING_AGENT_ID || "",
    ONBOARDING_SERVICE_ACCOUNT_ID: web.ONBOARDING_SERVICE_ACCOUNT_ID || "",
    DOC2COMPANY_AGENT_ID: web.DOC2COMPANY_AGENT_ID || "",
    DOC2COMPANY_SERVICE_ACCOUNT_ID: web.DOC2COMPANY_SERVICE_ACCOUNT_ID || "",
  });
};

const getConfigJson = async () => {
  // Relative path so it resolves against the <base href> set in index.html
  // and works for any path-prefixed deployment (e.g. preview /<pr-number>/).
  const config = await fetch("config.json");
  return await config.json();
};

const safeParseEnvironment = (
  env: EnvironmentSchema,
): EnvironmentSchema => {
  return environmentSchema.parse(env);
};

const getBuildTimeEnvironment = (): EnvironmentSchema => {
  return extractImportMetaEnv(import.meta.env as unknown as ImportMetaEnv);
};

const getRuntimeEnvironment = async (): Promise<EnvironmentSchema> => {
  return extractRunTimeEnv(await getConfigJson());
};

let config: EnvironmentSchema | undefined;

export const environment = async () => {
  if (config) {
    return config;
  }

  const isProduction = import.meta.env.MODE === "production";

  if (isProduction) {
    try {
      // Try runtime environment first in production
      const runtimeEnv = await getRuntimeEnvironment();
      config = safeParseEnvironment(runtimeEnv);
    } catch (error) {
      console.warn(
        "Runtime environment parsing failed, falling back to build time:",
        error
      );
      try {
        // Fallback to build time environment
        const buildTimeEnv = getBuildTimeEnvironment();
        config = safeParseEnvironment(buildTimeEnv);
      } catch (fallbackError) {
        console.error(
          "Both runtime and build time environment parsing failed:",
          fallbackError
        );
        throw new Error(
          "Failed to parse environment configuration from both runtime and build time sources"
        );
      }
    }
  } else {
    // Development mode - use build time environment
    const buildTimeEnv = getBuildTimeEnvironment();
    config = safeParseEnvironment(buildTimeEnv);
  }

  // Dev-only auth overrides must never take effect in a deployed (production) build,
  // regardless of what the runtime/build config supplied. Force them off.
  if (isProduction && (config.AUTH0_MOCK || config.DEV_MOCK_PROVISIONER)) {
    console.warn(
      "AUTH0_MOCK / DEV_MOCK_PROVISIONER are dev-only and were forced to false in this production build.",
    );
    config = { ...config, AUTH0_MOCK: false, DEV_MOCK_PROVISIONER: false };
  }

  return config;
};

export type Environment = typeof environment;
